import React from "react";
import "../../styles/DashboardHome.css";
import "../../styles/Dashboard.css";
import "../../styles/CVBuilder.css";
import "../../styles/CoverLetter.css";
import "../../styles/ResumeAnalyzer.css";
import {
  FiArrowRight,
  FiBriefcase,
  FiFileText,
  FiSearch,
  FiTarget,
} from "react-icons/fi";

import { canUse, track, getUsage } from "../../lib/plan";
import { useAnalyzer } from "../../features/analyzer/useAnalyzer";
import { useSavedJobs } from "../../lib/useSavedJobs";
import { supabase } from "../../lib/supabaseClient";

const API = process.env.REACT_APP_API ?? "http://localhost:8000";

type Props = {
  isSignedIn: boolean;
  userPlan: "free" | "premium";
  userId: string | null;
  onHitPaywall: () => void;

  resumeScore: number;
  atsMatch: number;

  jobsAnalyzed: number;
  coverLettersCreated: number;
  cvsCreated: number;

  recentAnalyses: {
    id: string;
    title: string;
    score: number;
    createdAt: string;
  }[];

  onOpenJobAnalyzer: () => void;
  onOpenResumeAnalyzer: () => void;
  onOpenCVBuilder: () => void;
  onOpenCoverLetter: () => void;

  onAtsMatchChange: (score: number) => void;
  onResumeScoreChange: (score: number) => void;
  onCoverLetterCreated: () => void;
  onRequireSignIn: () => void;

  onCvCreated: () => void;

  initialWorkspace?:
    | "none"
    | "job-analyzer"
    | "resume-analyzer"
    | "cv-builder"
    | "cover-letter";
};

function renderIcon(icon: unknown): React.ReactElement {
  return React.createElement(
    icon as React.ComponentType<Record<string, never>>
  );
}

export default function DashboardHome({
  isSignedIn,
  userId,
  userPlan,
  onHitPaywall,

  resumeScore,
  atsMatch,

  jobsAnalyzed,
  coverLettersCreated,
  cvsCreated,

  recentAnalyses,

  onOpenJobAnalyzer,
  onOpenResumeAnalyzer,
  onOpenCVBuilder,
  onOpenCoverLetter,
  onAtsMatchChange,
  onResumeScoreChange,
  onCvCreated,
  onCoverLetterCreated,
  onRequireSignIn,

  initialWorkspace = "none",
}: Props) {

  const { saved, savingId, save, unsave } = useSavedJobs();

  const quickActions = [
    {
      title: "Analyze a Job",
      description:
        "Compare a job description with your resume and identify the strongest improvements.",
      icon: FiSearch,
      action: () => {
        requireSignIn(() => {
          setWorkspaceMode("job-analyzer");

          setTimeout(() => {
            workspaceRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 100);
        });
      },
    },

    {
      title: "Review Your Resume",
      description:
        "Check your resume for clarity, relevance, formatting, and ATS compatibility.",
      icon: FiFileText,
      action: () =>
        requireSignIn(() => setWorkspaceMode("resume-analyzer")),
    },

    {
      title: "Build a CV",
      description:
        "Create a polished, professional CV tailored to your experience.",
      icon: FiBriefcase,
      action: () =>
        requireSignIn(() => setWorkspaceMode("cv-builder")),
    },

    {
      title: "Write a Cover Letter",
      description:
        "Generate a targeted cover letter that matches the role and company.",
      icon: FiTarget,
      action: () =>
        requireSignIn(() => setWorkspaceMode("cover-letter")),
    },
  ];

  const demoStats = {
    resumeScore: 94,
    atsMatch: 91,
    jobsAnalyzed: 18,
    cvsCreated: 7,
    coverLettersCreated: 12,
  };

  const metrics = [
    {
      label: "Resume Score",
      value: isSignedIn ? `${resumeScore}%` : `${demoStats.resumeScore}%`,
      note: "Excellent",
    },
    {
      label: "ATS Match",
      value: isSignedIn ? `${atsMatch}%` : `${demoStats.atsMatch}%`,
      note: "Highly Optimized",
    },
    {
      label: "Jobs Analyzed",
      value: String(
        isSignedIn ? jobsAnalyzed : demoStats.jobsAnalyzed
      ),
      note: "This Month",
    },
    {
      label: "CVs Created",
      value: String(
        isSignedIn ? cvsCreated : demoStats.cvsCreated
      ),
      note: "Generated",
    },
    {
      label: "Cover Letters",
      value: String(
        isSignedIn
          ? coverLettersCreated
          : demoStats.coverLettersCreated
      ),
      note: "Generated",
    },
  ];

  const demoAnalyses = [
    {
      id: "1",
      title: "Senior AI Engineer",
      createdAt: "Analyzed 2 hours ago",
      score: 94,
    },
    {
      id: "2",
      title: "Backend Developer",
      createdAt: "Analyzed 1 day ago",
      score: 89,
    },
    {
      id: "3",
      title: "Data Scientist",
      createdAt: "Analyzed 2 days ago",
      score: 86,
    },
  ];

  const analysesToShow = isSignedIn
    ? recentAnalyses
    : demoAnalyses;

  const [workspaceMode, setWorkspaceMode] = React.useState<
    "none" | "job-analyzer" | "cv-builder" | "cover-letter" | "resume-analyzer"
  >(initialWorkspace);

  React.useEffect(() => {
    if (initialWorkspace === "none") return;

    setWorkspaceMode(initialWorkspace);

    setTimeout(() => {
      workspaceRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }, [initialWorkspace]);

  const workspaceRef = React.useRef<HTMLDivElement>(null);

  const [jobDescription, setJobDescription] = React.useState("");
  const [jobUrl, setJobUrl] = React.useState("");
  const [resumeText, setResumeText] = React.useState("");

  const [cvBuilderInput, setCvBuilderInput] = React.useState("");
  const [generatedCv, setGeneratedCv] = React.useState("");
  const [cvLoading, setCvLoading] = React.useState(false);
  const [cvError, setCvError] = React.useState<string | null>(null);
  const [resumeFileName, setResumeFileName] = React.useState("");

  const [jobSearchLoading, setJobSearchLoading] = React.useState(false);
  const [jobSearchMessage, setJobSearchMessage] = React.useState("");

  const [cvLanguage, setCvLanguage] = React.useState<

    "en" | "sv" | "de" | "fr" | "es"
  >("en");

  const [jobSearchQuery, setJobSearchQuery] = React.useState(() => {
    return sessionStorage.getItem("jobtailor_search_query") || "";
  });

  const [jobSearchLocation, setJobSearchLocation] = React.useState(() => {
    return sessionStorage.getItem("jobtailor_search_location") || "";
  });

  const [jobSearchType, setJobSearchType] = React.useState(() => {
    return sessionStorage.getItem("jobtailor_search_type") || "Any";
  });

  const [jobSearchResults, setJobSearchResults] = React.useState<any[]>(() => {
    try {
      const raw = sessionStorage.getItem("jobtailor_search_results");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  React.useEffect(() => {
    sessionStorage.setItem(
      "jobtailor_search_query",
      jobSearchQuery
    );

    sessionStorage.setItem(
      "jobtailor_search_location",
      jobSearchLocation
    );

    sessionStorage.setItem(
      "jobtailor_search_type",
      jobSearchType
    );

    sessionStorage.setItem(
      "jobtailor_search_results",
      JSON.stringify(jobSearchResults)
    );
  }, [
    jobSearchQuery,
    jobSearchLocation,
    jobSearchType,
    jobSearchResults,
  ]);

  React.useEffect(() => {
    if (!isSignedIn) {
      setJobSearchResults([]);
      setJobSearchMessage("");
      setJobSearchLoading(false);

      sessionStorage.removeItem("jobtailor_search_query");
      sessionStorage.removeItem("jobtailor_search_location");
      sessionStorage.removeItem("jobtailor_search_type");
      sessionStorage.removeItem("jobtailor_search_results");
    }
  }, [isSignedIn]);

  React.useEffect(() => {
    if (!isSignedIn) {
      setWorkspaceMode("none");
      setJobDescription("");
      setJobUrl("");
      setResumeText("");
    }
  }, [isSignedIn]);

  const [usage, setUsage] = React.useState({
    search: 0,
    analyze: 0,
    export: 0,
  });

  const refreshUsage = React.useCallback(async () => {
    if (!isSignedIn) {
      setUsage({
        search: 0,
        analyze: 0,
        export: 0,
      });
      return;
    }

    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;

      if (!token) {
        setUsage({
          search: 0,
          analyze: 0,
          export: 0,
        });
        return;
      }

      const response = await fetch(`${API}/api/usage`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const raw = await response.text();

      if (!response.ok) {
        throw new Error(
          `Could not load usage (${response.status}): ${raw}`
        );
      }

      const result = JSON.parse(raw);

      setUsage({
        search: Number(result.search ?? 0),
        analyze: Number(result.ai ?? 0),
        export: Number(result.export ?? 0),
      });
    } catch (error) {
      console.error("[usage] refresh failed:", error);

      setUsage({
        search: 0,
        analyze: 0,
        export: 0,
      });
    }
  }, [API, isSignedIn]);

  React.useEffect(() => {
    void refreshUsage();
  }, [refreshUsage]);

  React.useEffect(() => {
    const handleUsageChanged = () => {
      void refreshUsage();
    };

    window.addEventListener(
      "dashboard:usage-changed",
      handleUsageChanged
    );

    return () => {
      window.removeEventListener(
        "dashboard:usage-changed",
        handleUsageChanged
      );
    };
  }, [refreshUsage]);

  const [resumeFile, setResumeFile] = React.useState<File | null>(null);

  const [cvAnalysis, setCvAnalysis] = React.useState("");
  const [cvAnalysisLoading, setCvAnalysisLoading] = React.useState(false);
  const [cvAnalysisError, setCvAnalysisError] = React.useState("");

  const [coverJobTitle, setCoverJobTitle] = React.useState("");
  const [coverCompany, setCoverCompany] = React.useState("");
  const [coverJobDescription, setCoverJobDescription] = React.useState("");
  const [coverLetter, setCoverLetter] = React.useState("");
  const [coverLetterLoading, setCoverLetterLoading] = React.useState(false);
  const [coverLetterError, setCoverLetterError] = React.useState("");

  const {
    analyze,
    loading: analysisLoading,
    error: analysisError,
    result: analysisResult,
  } = useAnalyzer();

  const handleDashboardAnalyze = async () => {
    if (!isSignedIn) {
      onRequireSignIn();
      return;
    }

    if (!jobDescription.trim() || !resumeText.trim()) {
      alert("Please add both the Job Description and your Resume.");
      return;
    }

    try {
      const data = await analyze({
        jobDescription,
        resumeText,
        language: "en",
        roleLevel: "mid",
        voiceStyle: "neutral",
      });

      const recentAnalysis = {
        id: crypto.randomUUID(),
        title: "Job Analysis",
        score: data.match_score ?? 0,
        createdAt: new Date().toISOString(),
      };

      onAtsMatchChange(data.match_score ?? 0);

      if (!userId) {
        return;
      }

      const storageKey =
        `jobtailor_recent_analyses_${userId}`;

      const existing = JSON.parse(
        localStorage.getItem(storageKey) ?? "[]"
      );

      const updated = [
        recentAnalysis,
        ...(Array.isArray(existing) ? existing : []),
      ].slice(0, 3);

      localStorage.setItem(
        storageKey,
        JSON.stringify(updated)
      );

      window.dispatchEvent(
        new Event("dashboard:analyses-changed")
      );

      window.dispatchEvent(
        new Event("dashboard:usage-changed")
      );
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === "FREE_LIMIT_REACHED"
      ) {
        onHitPaywall();
        return;
      }

      console.error("Dashboard analysis failed:", error);
    }
  };

  const handleResumeUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setResumeFileName(file.name);

    try {
      const form = new FormData();
      form.append("file", file);

      console.log("[resume upload] file:", file.name, file.type, file.size);
      console.log("[resume upload] POST:", `${API}/parse-resume`);

      const res = await fetch(`${API}/parse-resume`, {
        method: "POST",
        body: form,
      });

      const raw = await res.text();

      console.log("[resume upload] status:", res.status);
      console.log("[resume upload] response:", raw);

      if (!res.ok) {
        throw new Error(`Upload failed (${res.status}): ${raw}`);
      }
      const data = JSON.parse(raw);

      setResumeText(data.text || "");
      
    } catch (err) {
      console.error("[resume upload] ERROR:", err);
      alert("Could not read the resume file. Check the browser console.");
      setResumeFile(null);
    }
  };

  const handleDashboardJobSearch = async () => {
    if (!isSignedIn) {
      onRequireSignIn();
      return;
    }

    const query =
      jobSearchQuery.trim() || "software engineer";

    setJobSearchLoading(true);
    setJobSearchMessage("");
    setJobSearchResults([]);

    try {
      const { data: sessionData } =
        await supabase.auth.getSession();

      const token =
        sessionData.session?.access_token;

      if (!token) {
        onRequireSignIn();
        return;
      }

      const params = new URLSearchParams({
        q: query,
        sources: "platsbanken,adzuna",
        countries: "se",
      });

      if (jobSearchLocation.trim()) {
        params.append(
          "location",
          jobSearchLocation.trim()
        );
      }

      const res = await fetch(
        `${API}/api/jobs/search?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const raw = await res.text();

      if (res.status === 429) {
        onHitPaywall();
        return;
      }

      if (res.status === 401) {
        onRequireSignIn();
        return;
      }

      if (!res.ok) {
        throw new Error(
          `Search failed (${res.status}): ${raw}`
        );
      }

      const data = JSON.parse(raw);

      const results = Array.isArray(data)
        ? data
        : [];

      const queryWords = jobSearchQuery
        .toLowerCase()
        .split(/\s+/)
        .filter((word) => word.length > 2);

      const wantedLocation =
        jobSearchLocation.trim().toLowerCase();

      const filtered = results.filter(
        (job: any) => {
          const searchableText = [
            job.title,
            job.company,
            job.location,
            job.jd_text,
            job.description,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          const matchesQuery =
            queryWords.length === 0 ||
            queryWords.some((word) =>
              searchableText.includes(word)
            );

          const matchesLocation =
            !wantedLocation ||
            searchableText.includes(
              wantedLocation
            );

          const type = String(
            job.employment_type ??
              job.job_type ??
              job.contract_type ??
              ""
          ).toLowerCase();

          const matchesType =
            jobSearchType === "Any" ||
            type.includes(
              jobSearchType.toLowerCase()
            );

          return (
            matchesQuery &&
            matchesLocation &&
            matchesType
          );
        }
      );

      setJobSearchResults(filtered);

      if (filtered.length === 0) {
        setJobSearchMessage(
          `No jobs found for "${query}"${
            jobSearchLocation
              ? ` in ${jobSearchLocation}`
              : ""
          }.`
        );
      }

      window.dispatchEvent(
        new Event("dashboard:usage-changed")
      );
    } catch (err: any) {
      console.error(
        "Dashboard job search failed:",
        err
      );

      setJobSearchMessage(
        err?.message ||
          "Could not search jobs. Please try again."
      );
    } finally {
      setJobSearchLoading(false);
    }
  };

  const requireSignIn = (action: () => void) => {
    if (!isSignedIn) {
      onRequireSignIn();
      return;
    }

    action();
  };

  const handleAnalyzeJobResult = (job: any) => {
    const fullJobDescription = [
      job.title,
      job.company,
      job.location,
      job.jd_text,
    ]
      .filter(Boolean)
      .join("\n\n");

    requireSignIn(() => {
      setJobDescription(fullJobDescription);

      if (job.url) {
        setJobUrl(job.url);
      }

      setWorkspaceMode("job-analyzer");

      setTimeout(() => {
        workspaceRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    });
  };

  const handleGenerateCv = async () => {
    if (!isSignedIn) {
      onRequireSignIn();
      return;
    }

    if (!cvBuilderInput.trim()) {
      alert("Tell JobTailor about your background first.");
      return;
    }

    setCvLoading(true);
    setCvError(null);

    try {
      const { data: sessionData } =
      await supabase.auth.getSession();

    const token =
      sessionData.session?.access_token;

    if (!token) {
      onRequireSignIn();
      return;
    }

    const res = await fetch(`${API}/api/generate-cv`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        raw_text: cvBuilderInput.trim(),
        lang: cvLanguage,
      }),
    });

    if (res.status === 429) {
      onHitPaywall();
      return;
    }

    if (res.status === 401) {
      onRequireSignIn();
      return;
    }

    if (!res.ok) {
      const raw = await res.text();

      throw new Error(
        `CV generation failed (${res.status}): ${raw}`
      );
    }

      const data = await res.json();

      const generated = data.cv_text || "";

      setGeneratedCv(generated);

      if (generated.trim()) {
        onCvCreated();

        window.dispatchEvent(
          new Event("dashboard:usage-changed")
        );
      }
    } catch (err: any) {
      console.error("CV generation failed:", err);

      setCvError(
        err?.message ||
          "Could not generate your CV. Please try again."
      );
    } finally {
      setCvLoading(false);
    }
  };

  const handleExportCv = async () => {
    if (!generatedCv.trim()) {
      alert("No CV to export yet.");
      return;
    }

    try {
      const res = await fetch(`${API}/api/export/pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          what: "cv",
          uiLang: cvLanguage,
          cvText: generatedCv,
        }),
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(
          `Export failed (${res.status}): ${msg || "Unknown error"}`
        );
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      window.open(url, "_blank");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch (err) {
      console.error("CV export failed:", err);
      alert("Could not export the CV. Please try again.");
    }
  };

  const handleGenerateCoverLetter = async () => {
    if (!isSignedIn) {
      onRequireSignIn();
      return;
    }

    setCoverLetterError("");

    if (!coverJobTitle.trim()) {
      setCoverLetterError("Please add the job title.");
      return;
    }

    if (!coverCompany.trim()) {
      setCoverLetterError("Please add the company name.");
      return;
    }

    if (!coverJobDescription.trim()) {
      setCoverLetterError("Please add the job description.");
      return;
    }

    if (!resumeText.trim()) {
      setCoverLetterError("Please add or upload your CV/resume.");
      return;
    }

    try {
      setCoverLetterLoading(true);

      /*
        Include the job title and company in the JD text because
        the existing backend works from job_description.
      */
      const fullJobDescription = `
  Job title: ${coverJobTitle}
  Company: ${coverCompany}

  ${coverJobDescription}
      `.trim();

      const { data: sessionData } =
        await supabase.auth.getSession();

      const token =
        sessionData.session?.access_token;

      if (!token) {
        onRequireSignIn();
        return;
      }

      const response = await fetch(`${API}/generate/cover-letter`, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },

        body: JSON.stringify({
          job_description: fullJobDescription,
          resume_text: resumeText,
          lang: cvLanguage,
          tone: "neutral",
          richness: "rich",
        }),
      });

      const raw = await response.text();

      if (response.status === 429) {
        onHitPaywall();
        return;
      }

      if (response.status === 401) {
        onRequireSignIn();
        return;
      }

      console.log(
        "[cover letter] status:",
        response.status
      );

      console.log(
        "[cover letter] response:",
        raw
      );

      if (!response.ok) {
        throw new Error(
          `Cover letter generation failed (${response.status}): ${raw}`
        );
      }

      const data = JSON.parse(raw);

      const generated = data.cover_letter || "";

      setCoverLetter(generated);

      if (generated.trim()) {
        onCoverLetterCreated();

        window.dispatchEvent(
          new Event("dashboard:usage-changed")
        );
      }
    } catch (error) {
      console.error(
        "[cover letter] error:",
        error
      );

      setCoverLetterError(
        error instanceof Error
          ? error.message
          : "Could not generate the cover letter."
      );
    } finally {
      setCoverLetterLoading(false);
    }
  };

  const handleExportCoverLetterPdf = async () => {
    if (!coverLetter.trim()) return;

    try {
      const response = await fetch(`${API}/api/export/pdf`, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          what: "cover",
          uiLang: cvLanguage,
          coverLetter: coverLetter,
        }),
      });

      if (!response.ok) {
        const text = await response.text();

        throw new Error(
          `PDF export failed (${response.status}): ${text}`
        );
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = "jobtailor-cover-letter.pdf";

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(
        "[cover letter PDF] error:",
        error
      );

      setCoverLetterError(
        error instanceof Error
          ? error.message
          : "Could not export the cover letter PDF."
      );
    }
  };

  const handleAnalyzeCv = async () => {
    setCvAnalysisError("");

    if (!isSignedIn) {
      onRequireSignIn();
      return;
    }

    if (!resumeText.trim()) {
      setCvAnalysisError(
        "Please upload or paste your CV first."
      );
      return;
    }

    try {
      setCvAnalysisLoading(true);

      const { data: sessionData } =
        await supabase.auth.getSession();

      const token =
        sessionData.session?.access_token;

      if (!token) {
        onRequireSignIn();
        return;
      }

      const response = await fetch(`${API}/analyze-cv`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          resume_text: resumeText,
          lang: cvLanguage,
        }),
      });

      const raw = await response.text();

      if (response.status === 429) {
        onHitPaywall();
        return;
      }

      if (response.status === 401) {
        onRequireSignIn();
        return;
      }

      console.log("[CV Analyzer] status:", response.status);
      console.log("[CV Analyzer] response:", raw);

      if (!response.ok) {
        throw new Error(
          `CV analysis failed (${response.status}): ${raw}`
        );
      }

      const data = JSON.parse(raw);

      const report =
        data.analysis ??
        data.result ??
        data.report ??
        data.text ??
        "";

      setCvAnalysis(report);

      const score =
        typeof data.score === "number"
          ? data.score
          : Number(
              report.match(
                /OVERALL SCORE:\s*(\d{1,3})/i
              )?.[1] ?? 0
            );

      onResumeScoreChange(score);

      // Only charge quota when AI actually returned a report
      if (report.trim()) {
        window.dispatchEvent(
          new Event("dashboard:usage-changed")
        );
      }
    } catch (error) {
      console.error("[CV Analyzer] error:", error);

      setCvAnalysisError(
        error instanceof Error
          ? error.message
          : "Could not analyze the CV."
      );
    } finally {
      setCvAnalysisLoading(false);
    }
  };

  const handleExportCvAnalysisPdf = async () => {
    if (!cvAnalysis.trim()) return;

    try {
      const response = await fetch(`${API}/api/export/pdf`, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          what: "cv-analysis",
          uiLang: cvLanguage,
          cvAnalysis: cvAnalysis,
        }),
      });

      if (!response.ok) {
        const text = await response.text();

        throw new Error(
          `PDF export failed (${response.status}): ${text}`
        );
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = "jobtailor-cv-analysis.pdf";

      document.body.appendChild(link);

      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(
        "[CV analysis PDF] error:",
        error
      );

      setCvAnalysisError(
        error instanceof Error
          ? error.message
          : "Could not export CV analysis."
      );
    }
  };

  return (
    <section className="dashboard-home">
      <div className="dashboard-home__hero">
        <div className="dashboard-home__hero-content">
          <span className="dashboard-home__eyebrow">
            AI-POWERED JOB & CV ASSISTANT
          </span>

          <h2>Build stronger applications and land better opportunities.</h2>

          <p>
            Search jobs, analyze job matches, review and improve your CV, build tailored CVs, and create personalized cover letters - all in one place.
          </p>

          <div className="dashboard-home__hero-actions">
            <button
              type="button"
              onClick={() => {
                requireSignIn(() => {
                  setWorkspaceMode("job-analyzer");

                  setTimeout(() => {
                    workspaceRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }, 100);
                });
              }}
            >
              Analyze a Job
              {renderIcon(FiArrowRight)}
            </button>

            <button
              type="button"
              onClick={() => {
                requireSignIn(() => {
                  setWorkspaceMode("cv-builder");

                  setTimeout(() => {
                    workspaceRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }, 100);
                });
              }}
            >
              Build Your CV
            </button>
          </div>
        </div>
      </div>

      <section className="dashboard-job-search">
        <div className="dashboard-job-search__top">
          <div>
            <span>Find opportunities</span>
            <h3>Search jobs</h3>
          </div>

          <div className="dashboard-job-search__usage">
            {!isSignedIn ? (
              <>
                Searches: <strong>0/5</strong>
                {" • "}
                AI uses: <strong>0/3</strong>
              </>
            ) : userPlan === "premium" ? (
              <>
                Searches: <strong>Unlimited</strong>
                {" • "}
                AI uses: <strong>Unlimited</strong>
              </>
            ) : (
              <>
                Searches:{" "}
                <strong>{Math.min(usage.search, 5)}/5</strong>
                {" • "}
                AI uses:{" "}
                <strong>{Math.min(usage.analyze, 3)}/3</strong>
              </>
            )}
          </div>
        </div>
        
        <div className="dashboard-job-search__bar">
          <div className="dashboard-job-search__field dashboard-job-search__field--grow">
            <label>Role or keywords</label>
            <input
              type="text"
              value={jobSearchQuery}
              onChange={(e) => setJobSearchQuery(e.target.value)}
              placeholder="e.g. AI Engineer, React Developer..."
            />
          </div>

          <div className="dashboard-job-search__field">
            <label>Location</label>
            <input
              type="text"
              value={jobSearchLocation}
              onChange={(e) => setJobSearchLocation(e.target.value)}
              placeholder="Stockholm, London, Remote..."
            />
          </div>

          <div className="dashboard-job-search__field">
            <label>Type</label>
            <select
              value={jobSearchType}
              onChange={(e) => setJobSearchType(e.target.value)}
            >
              <option value="Any">Any</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Contract">Contract</option>
              <option value="Internship">Internship</option>
              <option value="Temporary">Temporary</option>
              <option value="Freelance">Freelance</option>
            </select>
          </div>

          <button
            type="button"
            className="dashboard-job-search__button"
            onClick={handleDashboardJobSearch}
            disabled={jobSearchLoading}
          >
            {jobSearchLoading ? "Searching…" : "Search Jobs"}
          </button>
        </div>

        {jobSearchMessage && (
          <p className="dashboard-job-search__message">
            {jobSearchMessage}
          </p>
        )}

        {jobSearchResults.length > 0 && (
          <div className="dashboard-job-search__results">
            {jobSearchResults.slice(0, 8).map((job, index) => (
              <article
                key={job.url ?? `${job.title}-${index}`}
                className="dashboard-job-search__result-card"
              >
                <div className="dashboard-job-search__result-main">
                  <div>
                    <h4>{job.title || "Untitled role"}</h4>

                    <p>
                      {job.company || "Unknown company"}
                    </p>

                    <span>
                      {job.location || "Location not specified"}
                    </span>
                  </div>

                  <div className="dashboard-job-search__result-actions">
                    {/* VIEW JOB */}
                    {job.url && (
                      <a
                        href={job.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Job
                      </a>
                    )}

                    {/* SAVE / UNSAVE JOB */}
                    {job.url && (
                      <button
                        type="button"
                        disabled={savingId === job.url}
                        onClick={() => {
                          if (!isSignedIn) {
                            onRequireSignIn();
                            return;
                          }

                          if (saved.has(job.url)) {
                            void unsave(job.url);
                          } else {
                            void save({
                              url: job.url,
                              title: job.title ?? undefined,
                              company: job.company ?? undefined,
                              location: job.location ?? undefined,
                              jd_text: job.description ?? undefined,
                            });
                          }
                        }}
                      >
                        {savingId === job.url
                          ? "Saving..."
                          : saved.has(job.url)
                            ? "✓ Saved"
                            : "Save Job"}
                      </button>
                    )}

                    {/* ANALYZE */}
                    <button
                      type="button"
                      onClick={() => handleAnalyzeJobResult(job)}
                    >
                      Analyze
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <div className="dashboard-home__metrics">
        {metrics.map((metric, index) => {
          const isScore = index < 2;

          return (
            <article
              key={metric.label}
              className="dashboard-home__metric-card"
            >
              <span>{metric.label}</span>

              {isScore ? (
                <div
                  className="dashboard-home__metric-ring"
                  style={{
                    ["--score" as string]:
                      metric.value.replace("%", ""),
                  }}
                >
                  <div>
                    <strong>{metric.value}</strong>
                  </div>
                </div>
              ) : (
                <strong className="dashboard-home__metric-number">
                  {metric.value}
                </strong>
              )}

              <small>{metric.note}</small>
            </article>
          );
        })}
      </div>

      <div className="dashboard-home__section-heading">
        <div>
          <span>Quick actions</span>
          <h3>What would you like to work on?</h3>
        </div>
      </div>

      <div className="dashboard-home__quick-grid">
        {quickActions.map((item) => (
          <button
            key={item.title}
            type="button"
            className="dashboard-home__quick-card"
            onClick={item.action}
          >
            <div className="dashboard-home__quick-icon">
              {renderIcon(item.icon)}
            </div>

            <div>
              <h4>{item.title}</h4>
              <p>{item.description}</p>
            </div>

            <span className="dashboard-home__quick-arrow">
              {renderIcon(FiArrowRight)}
            </span>
          </button>
        ))}
      </div>

      {/* ADD THE WORKSPACE HERE */}
      {workspaceMode !== "none" && (
        <section
          ref={workspaceRef}
          className="dashboard-workspace"
        >
          <div className="workspace-card__intro">
            <span>AI Workspace</span>
            <h3>Everything you need to build a stronger application.</h3>
            <p>
              Analyze jobs, optimize resumes, generate cover letters,
              and build tailored CVs.
            </p>
          </div>
          {/* AI WORKSPACE TABS */}
          <div className="dashboard-workspace-tabs">
            <button
              type="button"
              className={workspaceMode === "job-analyzer" ? "active" : ""}
              onClick={() =>
                requireSignIn(() => setWorkspaceMode("job-analyzer"))
              }
            >
              Job Analyzer
            </button>

            <button
              type="button"
              className={workspaceMode === "resume-analyzer" ? "active" : ""}
              onClick={() =>
                requireSignIn(() => setWorkspaceMode("resume-analyzer"))
              }
            >
              CV Analyzer
            </button>

            <button
              type="button"
              className={workspaceMode === "cv-builder" ? "active" : ""}
              onClick={() =>
                requireSignIn(() => setWorkspaceMode("cv-builder"))
              }
            >
              CV Builder
            </button>

            <button
              type="button"
              className={workspaceMode === "cover-letter" ? "active" : ""}
              onClick={() =>
                requireSignIn(() => setWorkspaceMode("cover-letter"))
              }
            >
              Cover Letter
            </button>
          </div>
          {/* JOB ANALYZER */}
          {workspaceMode === "job-analyzer" && (
            <>
              <div className="dashboard-workspace__header">
                <div>
                  <span>Job Analyzer</span>
                  <h3>Analyze a job before you apply</h3>

                  <p>
                    Paste the job description and compare it with your resume.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setWorkspaceMode("none")}
                >
                  ✕
                </button>
              </div>

              <div className="dashboard-workspace__grid">
                <div className="dashboard-workspace__card">
                  <h4>Job Description</h4>

                  <textarea
                    placeholder="Paste the job description here..."
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                  />

                  <div className="dashboard-workspace__divider">
                    <span>OR</span>
                  </div>

                  <div className="dashboard-workspace__url">
                    <input
                      type="url"
                      placeholder="Paste a job posting URL"
                      value={jobUrl}
                      onChange={(e) => setJobUrl(e.target.value)}
                    />
                  </div>
                </div>

                <div className="dashboard-workspace__card">
                  <h4>Your Resume</h4>

                  {/* Show uploaded / pasted CV text */}
                  <textarea
                    value={resumeText}
                    onChange={(e) => setResumeText(e.target.value)}
                    placeholder="Paste your CV text here..."
                  />

                  <div className="dashboard-workspace__divider">
                    <span>OR</span>
                  </div>

                  <label
                    className="dashboard-workspace__upload"
                    htmlFor="job-analyzer-cv-upload"
                  >
                    <div className="dashboard-workspace__upload-icon">
                      {renderIcon(FiFileText)}
                    </div>

                    <div>
                      <strong>
                        {resumeFileName ? `✓ ${resumeFileName}` : "Upload your CV"}
                      </strong>

                      <span>PDF, DOCX or TXT</span>
                    </div>

                    <input
                      id="job-analyzer-cv-upload"
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      hidden
                      onChange={handleResumeUpload}
                    />
                  </label>
                </div>
              </div>

              <div className="dashboard-workspace__actions">
                <button
                  type="button"
                  onClick={handleDashboardAnalyze}
                  disabled={analysisLoading}
                >
                  {analysisLoading ? "Analyzing…" : "Analyze Job"}
                </button>
              </div>

              {cvAnalysisError && (
                <div className="dashboard-workspace__error">
                  {cvAnalysisError}
                </div>
              )}

              {analysisResult && (
                <div className="dashboard-workspace__results">
                  <div className="dashboard-workspace__score">
                    <span>ATS Match</span>
                    <strong>{analysisResult.match_score}%</strong>
                  </div>

                  <div className="dashboard-workspace__result-grid">
                    <div className="dashboard-workspace__result-card">
                      <h4>Matched Keywords</h4>

                      <div className="dashboard-workspace__chips">
                        {analysisResult.top_common_keywords.map((keyword) => (
                          <span key={keyword}>{keyword}</span>
                        ))}
                      </div>
                    </div>

                    <div className="dashboard-workspace__result-card">
                      <h4>Missing Keywords</h4>

                      <div className="dashboard-workspace__chips dashboard-workspace__chips--missing">
                        {analysisResult.missing_keywords.map((keyword) => (
                          <span key={keyword}>{keyword}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {analysisResult.suggestions.length > 0 && (
                    <div className="dashboard-workspace__suggestions">
                      <h4>Resume Improvements</h4>

                      {analysisResult.suggestions.map((suggestion, index) => (
                        <article key={`${suggestion.original}-${index}`}>
                          <div>
                            <span>Current</span>
                            <p>{suggestion.original}</p>
                          </div>

                          <div>
                            <span>Suggested</span>
                            <p>{suggestion.rewrite}</p>
                          </div>

                          <small>{suggestion.reason}</small>
                        </article>
                      ))}
                    </div>
                  )}

                  <div className="dashboard-workspace__next">
                    <div>
                      <span>Ready for the next step?</span>
                      <h4>Turn this analysis into an application</h4>
                      <p>
                        Use the job and resume you already added. No need to paste everything again.
                      </p>
                    </div>

                    <div className="dashboard-workspace__next-actions">
                      <button
                        type="button"
                        className="dashboard-workspace__next-primary"
                        onClick={() => {
                          setWorkspaceMode("cv-builder");

                          setTimeout(() => {
                            workspaceRef.current?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                          }, 100);
                        }}
                      >
                        Build Tailored CV
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setWorkspaceMode("cover-letter");

                          setTimeout(() => {
                            workspaceRef.current?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                          }, 100);
                        }}
                      >
                        Generate Cover Letter
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* RESUME ANALYZER */}
          {workspaceMode === "resume-analyzer" && (
            <div className="dashboard-resume-analyzer">
              {/* Header */}
              <div className="dashboard-resume-analyzer__header">
                <div>
                  <span>AI CV ANALYZER</span>

                  <h3>Review and improve your CV</h3>

                  <p>
                    Upload your CV or paste the text below. JobTailor will analyze it
                    for clarity, impact, formatting, keywords, and ATS compatibility.
                  </p>
                </div>

                <button
                  type="button"
                  className="dashboard-resume-analyzer__close"
                  onClick={() => setWorkspaceMode("none")}
                  aria-label="Close Resume Analyzer"
                >
                  ✕
                </button>
              </div>

              {/* Main content */}
              <div className="dashboard-resume-analyzer__content">
                {/* LEFT SIDE */}
                <div className="dashboard-resume-analyzer__input">
                  <label>Your CV</label>

                  <textarea
                    value={resumeText}
                    onChange={(e) => setResumeText(e.target.value)}
                    placeholder="Paste your CV text here..."
                  />

                  <div className="dashboard-resume-analyzer__divider">
                    <span>OR</span>
                  </div>

                  <label
                    className="dashboard-resume-analyzer__upload"
                    htmlFor="resume-analyzer-upload"
                  >
                    <div className="dashboard-resume-analyzer__upload-icon">
                      ↥
                    </div>

                    <div>
                      <strong>Upload your CV</strong>
                      <span>PDF, DOCX or TXT</span>
                    </div>

                    <input
                      id="resume-analyzer-upload"
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      hidden
                      onChange={handleResumeUpload}
                    />
                  </label>

                  <p className="dashboard-resume-analyzer__hint">
                    JobTailor analyzes only the information contained in your CV and
                    will not invent experience, achievements, or qualifications.
                  </p>

                  <button
                    type="button"
                    className="dashboard-resume-analyzer__analyze"
                    onClick={handleAnalyzeCv}
                    disabled={cvAnalysisLoading || !resumeText.trim()}
                  >
                    {cvAnalysisLoading ? "Analyzing..." : "Analyze CV"}
                  </button>
                </div>

                {/* RIGHT SIDE */}
                <div className="dashboard-resume-analyzer__results">
                  <div className="dashboard-resume-analyzer__results-header">
                    <div>
                      <span>ANALYSIS</span>
                      <h4>Your CV Report</h4>
                    </div>

                    <div className="dashboard-resume-analyzer__actions">
                      <button
                        type="button"
                        onClick={handleExportCvAnalysisPdf}
                        disabled={!cvAnalysis.trim()}
                        title={
                          cvAnalysis.trim()
                            ? "Export CV analysis as PDF"
                            : "Analyze your CV first"
                        }
                      >
                        Export PDF
                      </button>

                      <button
                        type="button"
                        onClick={() => setCvAnalysis("")}
                        disabled={!cvAnalysis.trim()}
                        title={
                          cvAnalysis.trim()
                            ? "Clear CV analysis"
                            : "Nothing to clear yet"
                        }
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {cvAnalysis ? (
                    <textarea
                      className="dashboard-resume-analyzer__report"
                      value={cvAnalysis}
                      onChange={(e) => setCvAnalysis(e.target.value)}
                    />
                  ) : (
                    <div className="dashboard-resume-analyzer__empty">
                      <strong>Your CV analysis will appear here</strong>

                      <p>
                        Upload or paste your CV, then click Analyze CV.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* CV BUILDER */}
          {workspaceMode === "cv-builder" && (
            <div className="dashboard-cv-builder">
              <div className="dashboard-cv-builder__header">
                <div>
                  <span>AI CV BUILDER</span>

                  <h3>Build a professional CV</h3>

                  <p>
                    Tell JobTailor about your background in your own words. We'll organize
                    it into a polished, ATS-friendly CV.
                  </p>
                </div>

                <div className="dashboard-cv-builder__header-actions">
                  <select
                    value={cvLanguage}
                    onChange={(e) =>
                      setCvLanguage(
                        e.target.value as "en" | "sv" | "de" | "fr" | "es"
                      )
                    }
                  >
                    <option value="en">English</option>
                    <option value="sv">Svenska</option>
                    <option value="de">Deutsch</option>
                    <option value="fr">Français</option>
                    <option value="es">Español</option>
                  </select>

                  <button
                    type="button"
                    className="dashboard-cv-builder__close"
                    onClick={() => setWorkspaceMode("none")}
                    aria-label="Close CV Builder"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="dashboard-cv-builder__content">
                <div className="dashboard-cv-builder__input">
                  <label>Tell us about yourself</label>

                  <textarea
                    value={cvBuilderInput}
                    onChange={(e) => setCvBuilderInput(e.target.value)}
                    placeholder={`Example:

          I am John Erikson from Stockholm.

          I am targeting Project Manager roles.

          I worked at ABC Logistics from 2022 to present and coordinate shipments and logistics operations.

          Education:
          Bachelor of Business Administration, University of Nairobi, 2022

          Skills:
          Excel
          Power BI
          Customer Service

          Languages:
          English
          Swedish

          Certifications:
          PMP
          Scrum Master`}
                  />

                  <p className="dashboard-cv-builder__hint">
                    Include only real information you want in your CV. JobTailor will improve
                    the wording and structure but should not invent facts.
                  </p>

                  <button
                    type="button"
                    onClick={handleGenerateCv}
                    disabled={cvLoading || !cvBuilderInput.trim()}
                    className="dashboard-cv-builder__generate"
                  >
                    {cvLoading ? "Creating CV…" : "Generate CV"}
                  </button>

                  {cvError && (
                    <div className="dashboard-workspace__error">
                      {cvError}
                    </div>
                  )}
                </div>

                <div className="dashboard-cv-builder__preview">
                  <div className="dashboard-cv-builder__preview-header">
                    <div>
                      <span>Preview</span>
                      <h4>Your CV</h4>
                    </div>

                    <div className="dashboard-cv-builder__preview-actions">
                      <button
                        type="button"
                        onClick={handleExportCv}
                        disabled={!generatedCv.trim()}
                        title={
                          generatedCv.trim()
                            ? "Export CV as PDF"
                            : "Generate your CV first"
                        }
                      >
                        Export PDF
                      </button>

                      <button
                        type="button"
                        onClick={() => setGeneratedCv("")}
                        disabled={!generatedCv.trim()}
                        title={
                          generatedCv.trim()
                            ? "Clear CV"
                            : "Nothing to clear yet"
                        }
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {generatedCv ? (
                    <>
                      <textarea
                        className="dashboard-cv-builder__document"
                        value={generatedCv}
                        onChange={(e) => setGeneratedCv(e.target.value)}
                      />

                      <div className="dashboard-cv-builder__document-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setResumeText(generatedCv);
                            setWorkspaceMode("job-analyzer");

                            setTimeout(() => {
                              workspaceRef.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }, 100);
                          }}
                        >
                          Use as Resume
                        </button>

                        <button
                          type="button"
                          onClick={handleExportCv}
                          className="dashboard-cv-builder__export-primary"
                        >
                          Export PDF
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="dashboard-cv-builder__empty">
                      <strong>Your generated CV will appear here</strong>
                      <p>
                        Add your real background information and click Generate CV.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* COVER LETTER */}
          {workspaceMode === "cover-letter" && (
            <div className="dashboard-cover-letter">
              <div className="dashboard-cover-letter__header">
                <div>
                  <span>AI COVER LETTER</span>

                  <h3>Create a tailored cover letter</h3>

                  <p>
                    Add the role, company and job description. JobTailor will create a
                    professional cover letter based only on your real background.
                  </p>
                </div>

                <div className="dashboard-cover-letter__header-actions">
                  <select
                    value={cvLanguage}
                    onChange={(e) =>
                      setCvLanguage(
                        e.target.value as "en" | "sv" | "de" | "fr" | "es"
                      )
                    }
                  >
                    <option value="en">English</option>
                    <option value="sv">Svenska</option>
                    <option value="de">Deutsch</option>
                    <option value="fr">Français</option>
                    <option value="es">Español</option>
                  </select>

                  <button
                    type="button"
                    className="dashboard-cover-letter__close"
                    onClick={() => setWorkspaceMode("none")}
                    aria-label="Close Cover Letter"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="dashboard-cover-letter__content">
                <div className="dashboard-cover-letter__form">
                  <div className="dashboard-cover-letter__field">
                    <label>Job title</label>

                    <input
                      type="text"
                      value={coverJobTitle}
                      onChange={(e) => setCoverJobTitle(e.target.value)}
                      placeholder="e.g. AI Engineer"
                    />
                  </div>

                  <div className="dashboard-cover-letter__field">
                    <label>Company</label>

                    <input
                      type="text"
                      value={coverCompany}
                      onChange={(e) => setCoverCompany(e.target.value)}
                      placeholder="e.g. Volvo Group"
                    />
                  </div>

                  <div className="dashboard-cover-letter__field">
                    <label>Job description</label>

                    <textarea
                      value={coverJobDescription}
                      onChange={(e) => setCoverJobDescription(e.target.value)}
                      placeholder="Paste the job description here..."
                    />
                  </div>

                  <div className="dashboard-cover-letter__field">
                    <label>Your background</label>

                    <textarea
                      value={resumeText}
                      onChange={(e) => setResumeText(e.target.value)}
                      placeholder="Paste your resume or describe your experience..."
                    />
                  </div>

                  <p className="dashboard-cover-letter__hint">
                    JobTailor should improve the writing but should not invent experience,
                    achievements or qualifications.
                  </p>

                  <div className="dashboard-cover-letter__actions">
                    <label className="dashboard-cover-letter__upload-button">
                      {resumeFileName ? `✓ ${resumeFileName}` : "Upload CV"}

                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.txt"
                        onChange={handleResumeUpload}
                      />
                    </label>

                    <button
                      type="button"
                      className="dashboard-cover-letter__generate"
                      onClick={handleGenerateCoverLetter}
                      disabled={
                        coverLetterLoading ||
                        !coverJobTitle.trim() ||
                        !coverCompany.trim() ||
                        !coverJobDescription.trim() ||
                        !resumeText.trim()
                      }
                    >
                      {coverLetterLoading
                        ? "Generating..."
                        : "Generate Cover Letter"}
                    </button>
                  </div>

                  {/* Cover Letter error message */}
                  {coverLetterError && (
                    <div className="dashboard-workspace__error">
                      {coverLetterError}
                    </div>
                  )}
                </div>

                <div className="dashboard-cover-letter__preview">
                  <div className="dashboard-cover-letter__preview-header">
                    <div>
                      <span>PREVIEW</span>
                      <h4>Your Cover Letter</h4>
                    </div>

                    <div className="dashboard-cover-letter__preview-actions">
                      <button
                        type="button"
                        onClick={handleExportCoverLetterPdf}
                        disabled={!coverLetter.trim()}
                        title={
                          coverLetter.trim()
                            ? "Export cover letter as PDF"
                            : "Generate a cover letter first"
                        }
                      >
                        Export PDF
                      </button>

                      <button
                        type="button"
                        onClick={() => setCoverLetter("")}
                        disabled={!coverLetter.trim()}
                        title={
                          coverLetter.trim()
                            ? "Clear cover letter"
                            : "Nothing to clear yet"
                        }
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {coverLetter ? (
                    <textarea
                      value={coverLetter}
                      onChange={(e) => setCoverLetter(e.target.value)}
                      className="dashboard-cover-letter__document"
                    />
                  ) : (
                    <div className="dashboard-cover-letter__empty">
                      <strong>Your generated cover letter will appear here</strong>

                      <p>
                        Add the job details and your real background, then click Generate
                        Cover Letter.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      <div className="dashboard-home__bottom-grid">
        <article className="dashboard-home__panel">
          <div className="dashboard-home__panel-header">
            <div>
              <h3>Recent Analysis</h3>
            </div>
          </div>

          <div className="dashboard-home__recent-list">
            {!isSignedIn ? (
              analysesToShow.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="dashboard-home__recent-item"
                  onClick={onOpenResumeAnalyzer}
                >
                  <div className="dashboard-home__recent-icon">
                    {renderIcon(FiFileText)}
                  </div>

                  <div className="dashboard-home__recent-copy">
                    <strong>{item.title}</strong>
                    <span>{item.createdAt}</span>
                  </div>

                  <div className="dashboard-home__recent-score">
                    {item.score}%
                  </div>

                  <span className="dashboard-home__recent-action">
                    View Report
                  </span>
                </button>
              ))
            ) : recentAnalyses.length === 0 ? (
              <div className="dashboard-home__empty-state">
                <div>{renderIcon(FiFileText)}</div>

                <h4>No recent analyses yet</h4>

                <p>
                  Analyze your first job description to start building your
                  dashboard history.
                </p>

                <button type="button" onClick={onOpenResumeAnalyzer}>
                  Start Your First Analysis
                </button>
              </div>
            ) : (
              recentAnalyses.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="dashboard-home__recent-item"
                  onClick={onOpenResumeAnalyzer}
                >
                  <div className="dashboard-home__recent-icon">
                    {renderIcon(FiFileText)}
                  </div>

                  <div className="dashboard-home__recent-copy">
                    <strong>{item.title}</strong>
                    <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                  </div>

                  <div className="dashboard-home__recent-score">
                    {item.score}%
                  </div>

                  <span className="dashboard-home__recent-action">
                    View Report
                  </span>
                </button>
              ))
            )}
            <div className="dashboard-home__cta">
              <div>
                <strong>Tailored Applications. Better Results.</strong>
                <span>Create unique applications that get noticed.</span>
              </div>

              <button type="button" onClick={onOpenResumeAnalyzer}>
                Start New Analysis
                {renderIcon(FiArrowRight)}
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

